import { useState, useEffect, useCallback, useRef } from "react";
import { apiCall } from "../api";
import { RoleBadge } from "./common/Badge";

const MODULE_OPTIONS = [
  { value: "", label: "All Modules" },
  { value: "users", label: "Users" },
  { value: "machines", label: "Machines" },
  { value: "pm_plans", label: "PM Plans" },
  { value: "spare_parts", label: "Spare Parts" },
  { value: "history", label: "History" },
  { value: "departments", label: "Departments" },
  { value: "revisions", label: "Revisions" },
  { value: "backup", label: "Backup" },
  { value: "hr_export", label: "HR Export" },
  { value: "checksheets", label: "Checksheets" },
  { value: "units", label: "Units" },
];

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "deleted", label: "Deleted" },
  { value: "restored", label: "Restored" },
  // Written by the HR monthly JSON export. Without it here the entries are
  // still logged and visible under "All Actions", just not filterable.
  { value: "downloaded", label: "Downloaded" },
];

const ACTION_COLORS = {
  created: "bg-green-100 text-green-700",
  updated: "bg-blue-100 text-blue-700",
  deleted: "bg-red-100 text-red-700",
  restored: "bg-amber-100 text-amber-700",
  downloaded: "bg-indigo-100 text-indigo-700",
};

function formatDate(isoStr) {
  if (!isoStr) return "-";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AuditLog() {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const debounceRef = useRef(null);

  const fetchLogs = useCallback(async (p, mod, act, srch) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: 50 });
      if (mod) params.set("module", mod);
      if (act) params.set("action", act);
      if (srch) params.set("search", srch);
      const data = await apiCall("GET", `/audit-logs?${params.toString()}`);
      setLogs(data.logs);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    apiCall("GET", "/audit-logs/summary").then(setSummary).catch(() => {});
  }, []);

  useEffect(() => {
    fetchLogs(page, module, action, search);
  }, [page, module, action, search, fetchLogs]);

  function handleSearchInput(val) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  }

  function handleModuleChange(val) { setModule(val); setPage(1); }
  function handleActionChange(val) { setAction(val); setPage(1); }

  // Top 3 modules from summary
  const topModules = summary
    ? Object.entries(summary.by_module)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
    : [];

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      {/* Summary row */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="audit-summary">
        <span className="text-sm text-gray-600">
          Total: <strong data-testid="audit-total-count">{summary?.total ?? "..."}</strong> logs
        </span>
        {topModules.map(([mod, count]) => (
          <span key={mod} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium" data-testid={`summary-module-${mod}`}>
            {mod}: {count}
          </span>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex gap-3 items-end flex-wrap" data-testid="audit-filters">
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Module</label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={module}
            onChange={e => handleModuleChange(e.target.value)}
            data-testid="audit-filter-module"
          >
            {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Action</label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={action}
            onChange={e => handleActionChange(e.target.value)}
            data-testid="audit-filter-action"
          >
            {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-gray-500 font-medium block mb-1">Search</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            placeholder="Search by user, target, or details..."
            defaultValue=""
            onChange={e => handleSearchInput(e.target.value)}
            data-testid="audit-filter-search"
          />
        </div>
      </div>

      {/* Log table */}
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="audit-log-table">
        {loading ? (
          <div className="text-sm text-gray-400 py-8 text-center">Loading...</div>
        ) : total === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center" data-testid="audit-empty">No audit logs found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                {["Timestamp", "User", "Action", "Module", "Target", "Details"].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="border-b hover:bg-gray-50" data-testid={`audit-row-${log.id}`}>
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(log.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-800 text-xs font-medium">{log.user_name}</span>
                      <RoleBadge role={log.user_role} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[log.action] || "bg-gray-100 text-gray-600"}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{log.module}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs">{log.target_name || log.target_id || "\u2014"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px]" title={log.details || ""}>
                    {log.details ? (log.details.length > 60 ? log.details.slice(0, 60) + "..." : log.details) : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between" data-testid="audit-pagination">
          <span className="text-xs text-gray-500" data-testid="audit-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-prev-btn"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-next-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
